/**
 * 集合页筛选器增强：价格双滑块同步、区间文案更新、drawer Apply 提交价格
 */
(function () {
  /**
   * 价格向上取整到分
   * @param {number} value
   * @returns {number}
   */
  function ceilPrice(value) {
    if (!Number.isFinite(value)) return 0;
    return Math.ceil(value * 100) / 100;
  }

  /**
   * @param {number} value
   * @param {number} rangeMax
   * @returns {boolean}
   */
  function isMaxAtRange(value, rangeMax) {
    return Math.abs(ceilPrice(value) - ceilPrice(rangeMax)) < 0.001;
  }

  /**
   * 当前语言的小数分隔符（de/fr/nl 为逗号）
   * @returns {string}
   */
  function decimalSeparator() {
    try {
      const parts = new Intl.NumberFormat(document.documentElement.lang || undefined).formatToParts(1.1);
      const hit = parts.find((part) => part.type === 'decimal');
      return hit ? hit.value : '.';
    } catch (_) {
      return '.';
    }
  }

  function formatPriceInput(value) {
    const normalized = ceilPrice(value);
    if (normalized <= 0) return '';
    const raw = Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(2);
    const sep = decimalSeparator();
    return sep === ',' ? raw.replace('.', ',') : raw;
  }

  /**
   * 解析本地化金额字符串（支持 1,234.56 / 1.234,56 / 186,99）
   * @param {string} value
   * @returns {number}
   */
  function parseLocalizedNumber(value) {
    if (value == null) return NaN;
    let raw = String(value).trim().replace(/\s/g, '').replace(/'/g, '');
    if (!raw) return NaN;

    const hasComma = raw.includes(',');
    const hasDot = raw.includes('.');
    if (hasComma && hasDot) {
      if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
        raw = raw.replace(/\./g, '').replace(',', '.');
      } else {
        raw = raw.replace(/,/g, '');
      }
    } else if (hasComma) {
      const parts = raw.split(',');
      const last = parts[parts.length - 1] || '';
      if (parts.length > 2 || last.length === 3) {
        raw = raw.replace(/,/g, '');
      } else {
        raw = raw.replace(',', '.');
      }
    }

    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  /**
   * @param {number} value
   * @param {string} [currencyCode] 零小数币种（JPY 等）不带小数位
   * @returns {string}
   */
  function formatMoneyDisplay(value, currencyCode) {
    const normalized = ceilPrice(value);
    const zeroDecimal = new Set(['JPY', 'KRW', 'VND', 'CLP', 'ISK', 'HUF', 'TWD', 'XAF', 'XOF', 'XPF']);
    if (zeroDecimal.has(String(currencyCode || '').toUpperCase()) || Number.isInteger(normalized)) {
      return String(Math.round(normalized));
    }
    return normalized.toFixed(2);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {number}
   */
  function readInputValue(input) {
    if (!input) return 0;
    const parsed = parseLocalizedNumber(input.value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * 将滑块当前值写回价格文本框，供 updatePriceFilterAndResults 读取
   * @param {HTMLElement} component
   */
  function commitPriceSliderValues(component) {
    const slider = component.querySelector('.price-facet__slider');
    const minRange = component.querySelector('.price-facet__range--min');
    const maxRange = component.querySelector('.price-facet__range--max');
    const minInput = component.querySelector('[ref="minInput"], [name*="price.gte"]');
    const maxInput = component.querySelector('[ref="maxInput"], [name*="price.lte"]');

    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const rangeMax = parseLocalizedNumber(slider?.dataset.rangeMax || maxRange.max || '0') || 0;
    let minVal = parseLocalizedNumber(minRange.value);
    let maxVal = parseLocalizedNumber(maxRange.value);

    if (!Number.isFinite(minVal)) minVal = 0;
    if (!Number.isFinite(maxVal)) maxVal = rangeMax;

    minVal = ceilPrice(minVal);
    maxVal = ceilPrice(maxVal);

    if (minVal > maxVal) {
      minVal = maxVal;
      minRange.value = String(minVal);
    }

    // 提交给 Shopify 的值必须用点号小数，逗号只给展示层
    if (minInput instanceof HTMLInputElement) {
      minInput.value = minVal <= 0 ? '' : formatMoneyDisplay(minVal);
    }
    if (maxInput instanceof HTMLInputElement) {
      maxInput.value = isMaxAtRange(maxVal, rangeMax) ? '' : formatMoneyDisplay(maxVal);
    }
  }

  /** 连续筛选时作废上一次等待，避免旧回调重复 init */
  let morphWaitGeneration = 0;

  /** Apply 主动关闭 drawer 时，filter morph 后不再重新打开 */
  let suppressDrawerReopen = false;
  let suppressDrawerReopenTimer = 0;

  /** 标记用户点击 Apply 关闭 drawer，短时内忽略 morph 后的 reopen */
  function markDrawerClosedByApply() {
    suppressDrawerReopen = true;
    /* 作废已排队的 morph reopen 回调 */
    morphWaitGeneration += 1;
    window.clearTimeout(suppressDrawerReopenTimer);
    suppressDrawerReopenTimer = window.setTimeout(() => {
      suppressDrawerReopen = false;
    }, 3000);
  }

  /**
   * filter:update 早于异步 morph，等区块 DOM 变更后再初始化
   * @param {() => void} callback
   */
  function afterFilterSectionMorph(callback) {
    const waitId = ++morphWaitGeneration;
    const sectionEl =
      document.querySelector('.product-grid-container[data-section-id]') ||
      document.getElementById('ResultsList') ||
      document.querySelector('facets-form-component');

    if (!sectionEl) {
      window.requestAnimationFrame(() => {
        if (waitId === morphWaitGeneration) callback();
      });
      return;
    }

    let settled = false;
    let debounceTimer = 0;
    let fallbackTimer = 0;

    const cleanup = () => {
      window.clearTimeout(debounceTimer);
      window.clearTimeout(fallbackTimer);
      observer.disconnect();
    };

    /* generation 失效时也必须 disconnect，避免连续筛选泄漏 observer */
    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      if (waitId === morphWaitGeneration) callback();
    };

    const observer = new MutationObserver(() => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(finish, 80);
    });

    /* 只观察子树增删，避免 dialog class/attribute 抖动导致过早 settle */
    observer.observe(sectionEl, {
      childList: true,
      subtree: true,
    });

    fallbackTimer = window.setTimeout(finish, 2000);
  }

  /**
   * @param {HTMLElement} component
   */
  function initPriceSlider(component) {
    if (!(component instanceof HTMLElement)) return;

    if (component._ugPriceAbort instanceof AbortController) {
      component._ugPriceAbort.abort();
    }
    const abortController = new AbortController();
    component._ugPriceAbort = abortController;
    const { signal } = abortController;

    const slider = component.querySelector('.price-facet__slider');
    const minRange = component.querySelector('.price-facet__range--min');
    const maxRange = component.querySelector('.price-facet__range--max');
    const minInput = component.querySelector('[ref="minInput"], [name*="price.gte"]');
    const maxInput = component.querySelector('[ref="maxInput"], [name*="price.lte"]');
    const rangeDisplay = component.querySelector('[data-price-range-display]');
    const fill = component.querySelector('.price-facet__slider-fill');

    if (!(slider instanceof HTMLElement) || !(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) {
      return;
    }

    const rangeMax = parseLocalizedNumber(slider.dataset.rangeMax || maxRange.max || '0') || 0;
    const currencySymbol = slider.dataset.currencySymbol || '';
    const currencyCode = (slider.dataset.currencyCode || '').toUpperCase();
    const isDrawer = Boolean(component.closest('.facets--drawer'));

    /**
     * @param {number} minVal
     * @param {number} maxVal
     */
    function updateRangeDisplay(minVal, maxVal) {
      if (!(rangeDisplay instanceof HTMLElement)) return;

      const minMoney = `${currencySymbol}${formatMoneyDisplay(minVal, currencyCode)}`;
      const maxMoney = `${currencySymbol}${formatMoneyDisplay(isMaxAtRange(maxVal, rangeMax) ? rangeMax : maxVal, currencyCode)}`;

      if (isDrawer) {
        rangeDisplay.textContent = `${minMoney} – ${maxMoney}`;
      } else {
        const priceItem = component.closest('.facets__item--price');
        const label =
          priceItem?.querySelector('.facets__price-heading')?.textContent?.trim() ||
          priceItem?.querySelector('.facets__label')?.textContent?.trim() ||
          slider.dataset.priceLabel ||
          '';
        rangeDisplay.textContent = `${label}: ${minMoney} - ${maxMoney}`;
      }
    }

    /**
     * @param {number} minVal
     * @param {number} maxVal
     */
    function updateFill(minVal, maxVal) {
      if (!(fill instanceof HTMLElement) || rangeMax <= 0) return;

      const minPercent = Math.min(Math.max((ceilPrice(minVal) / rangeMax) * 100, 0), 100);
      const maxPercent = Math.min(Math.max((ceilPrice(maxVal) / rangeMax) * 100, 0), 100);

      fill.style.left = `${minPercent}%`;
      fill.style.width = `${Math.max(maxPercent - minPercent, 0)}%`;
    }

    /**
     * @param {number} minVal
     * @param {number} maxVal
     */
    function syncRangeInputs(minVal, maxVal) {
      minRange.value = String(ceilPrice(minVal));
      maxRange.value = String(ceilPrice(maxVal));
    }

    /**
     * 从文本输入框同步到滑块
     */
    function clampPriceRange(minVal, maxVal) {
      if (!Number.isFinite(minVal) || minVal < 0) minVal = 0;
      if (!Number.isFinite(maxVal) || maxVal <= 0) maxVal = rangeMax;
      if (maxVal > rangeMax) maxVal = rangeMax;
      if (minVal > rangeMax) minVal = rangeMax;
      if (minVal > maxVal) minVal = maxVal;
      return [ceilPrice(minVal), ceilPrice(maxVal)];
    }

    function syncFromInputs() {
      let minVal = minInput instanceof HTMLInputElement ? readInputValue(minInput) : Number.parseFloat(minRange.value);
      let maxVal = maxInput instanceof HTMLInputElement ? readInputValue(maxInput) : Number.parseFloat(maxRange.value);
      [minVal, maxVal] = clampPriceRange(minVal, maxVal);

      syncRangeInputs(minVal, maxVal);

      if (minInput instanceof HTMLInputElement && minInput.value.trim() !== '') {
        minInput.value = formatPriceInput(minVal);
      }
      if (maxInput instanceof HTMLInputElement && maxInput.value.trim() !== '') {
        maxInput.value = formatPriceInput(maxVal);
      }

      updateFill(minVal, maxVal);
      updateRangeDisplay(minVal, maxVal);
    }

    /**
     * 从滑块同步区间 UI。
     * Drawer 未操作过的输入框保持占位灰态，只有编辑或拖滑块后才写入并高亮。
     * 提交仍由 Apply / commitPriceSliderValues 负责。
     * @param {{ fromUser?: boolean }} [options]
     */
    function syncFromSliders(options = {}) {
      let minVal = Number.parseFloat(minRange.value);
      let maxVal = Number.parseFloat(maxRange.value);
      [minVal, maxVal] = clampPriceRange(minVal, maxVal);

      if (minVal > maxVal) {
        if (document.activeElement === minRange) {
          maxVal = minVal;
          maxRange.value = String(maxVal);
        } else {
          minVal = maxVal;
          minRange.value = String(minVal);
        }
      }

      const fromUser = options.fromUser === true;
      if (fromUser && isDrawer) {
        if (minInput instanceof HTMLInputElement) minInput.classList.add('is-active');
        if (maxInput instanceof HTMLInputElement) maxInput.classList.add('is-active');
      }

      // 桌面不写带 name 的输入，避免拖滑块后勾其它筛选项把未确认价格带走。
      // Drawer 只在用户操作后写回展示值；真正提交仍走 Apply / commitPriceSliderValues。
      if (isDrawer) {
        if (minInput instanceof HTMLInputElement && minInput.classList.contains('is-active')) {
          minInput.value = formatPriceInput(minVal);
        }
        if (maxInput instanceof HTMLInputElement && maxInput.classList.contains('is-active')) {
          maxInput.value = formatPriceInput(maxVal);
        }
      }
      updateFill(minVal, maxVal);
      updateRangeDisplay(minVal, maxVal);
    }

    /**
     * 重叠时抬高可操作拇指：左半区抬 max，右半区抬 min；按下时抬当前拇指
     * @param {HTMLInputElement} [active]
     */
    function updateThumbStacking(active) {
      if (active instanceof HTMLInputElement) {
        const other = active === minRange ? maxRange : minRange;
        active.style.zIndex = '4';
        other.style.zIndex = '3';
        return;
      }

      const minVal = Number.parseFloat(minRange.value);
      const maxVal = Number.parseFloat(maxRange.value);
      if (!Number.isFinite(minVal) || !Number.isFinite(maxVal)) return;

      if (Math.abs(minVal - maxVal) < 0.001) {
        if (minVal <= rangeMax / 2) {
          maxRange.style.zIndex = '4';
          minRange.style.zIndex = '3';
        } else {
          minRange.style.zIndex = '4';
          maxRange.style.zIndex = '3';
        }
      }
    }

    minRange.addEventListener('input', () => {
      syncFromSliders({ fromUser: true });
      updateThumbStacking();
    }, { signal });
    maxRange.addEventListener('input', () => {
      syncFromSliders({ fromUser: true });
      updateThumbStacking();
    }, { signal });
    minRange.addEventListener('pointerdown', () => updateThumbStacking(minRange), { signal });
    maxRange.addEventListener('pointerdown', () => updateThumbStacking(maxRange), { signal });

    if (minInput instanceof HTMLInputElement) {
      minInput.addEventListener('input', () => {
        minInput.classList.add('is-active');
        syncFromInputs();
      }, { signal });
      minInput.addEventListener('focus', () => {
        requestAnimationFrame(() => minInput.select());
      }, { signal });
    }
    if (maxInput instanceof HTMLInputElement) {
      maxInput.addEventListener('input', () => {
        maxInput.classList.add('is-active');
        syncFromInputs();
      }, { signal });
      maxInput.addEventListener('focus', () => {
        requestAnimationFrame(() => maxInput.select());
      }, { signal });
    }

    if (isDrawer) {
      if (minInput instanceof HTMLInputElement && minInput.value.trim() !== '') {
        minInput.classList.add('is-active');
      }
      if (maxInput instanceof HTMLInputElement && maxInput.value.trim() !== '') {
        maxInput.classList.add('is-active');
      }
    }

    syncFromSliders();
    updateThumbStacking();
  }

  function initAllPriceSliders() {
    document.querySelectorAll('price-facet-component').forEach((component) => {
      if (component instanceof HTMLElement) {
        initPriceSlider(component);
      }
    });
  }

  /**
   * drawer 底部 Apply：先提交价格筛选，再关闭弹层
   */
  function patchDialogForDrawerApply() {
    customElements.whenDefined('dialog-component').then(() => {
      const DialogComponent = customElements.get('dialog-component');
      if (!DialogComponent || DialogComponent.prototype.applyDrawerFiltersAndClose) return;

      DialogComponent.prototype.applyDrawerFiltersAndClose = function () {
        markDrawerClosedByApply();

        const priceComponent = this.querySelector('.facets--drawer price-facet-component');
        if (priceComponent && typeof priceComponent.updatePriceFilterAndResults === 'function') {
          commitPriceSliderValues(priceComponent);
          priceComponent.updatePriceFilterAndResults();
        }

        this.closeDialog();
      };
    });
  }

  function patchDesktopPriceFilterApply() {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const btn = target.closest('.price-facet__apply-btn');
        if (!(btn instanceof HTMLElement)) return;
        const priceComponent = btn.closest('price-facet-component');
        if (!(priceComponent instanceof HTMLElement)) return;

        // 先把滑块值写入带 name 的输入；再主动提交，避免仅依赖 declarative on:click 时序
        commitPriceSliderValues(priceComponent);
        if (typeof priceComponent.updatePriceFilterAndResults === 'function') {
          event.preventDefault();
          event.stopImmediatePropagation();
          priceComponent.updatePriceFilterAndResults();
        }
      },
      true
    );
  }

  /** @returns {string} */
  function getCollectionDefaultSortBy() {
    const el = document.querySelector('[data-default-sort-by]');
    return el instanceof HTMLElement ? el.getAttribute('data-default-sort-by') || '' : '';
  }

  /**
   * 价格框里存的是本地化展示值（de/fr/nl 下小数点是逗号），Shopify 只认点号。
   * 勾任意筛选项都会整表提交，所以在唯一的出口这里统一归一化。
   * @param {URLSearchParams} params
   */
  function normalizePriceParams(params) {
    for (const key of ['filter.v.price.gte', 'filter.v.price.lte']) {
      const raw = params.get(key);
      if (!raw) continue;
      const parsed = parseLocalizedNumber(raw);
      if (!Number.isFinite(parsed)) {
        params.delete(key);
        continue;
      }
      params.set(key, formatMoneyDisplay(parsed));
    }
  }

  /**
   * - 移动端 Sort 独立 form 只有 sort_by：合并当前 URL 的 filter.*，避免清空已选筛选
   * - 默认排序不写进 URL（与集合 default_sort_by 相同时删除 sort_by）
   */
  function patchFacetsUrlParameters() {
    customElements.whenDefined('facets-form-component').then(() => {
      const FacetsFormComponent = customElements.get('facets-form-component');
      if (!FacetsFormComponent || FacetsFormComponent.prototype.__ugSortPreservePatched) return;

      const originalCreateURLParameters = FacetsFormComponent.prototype.createURLParameters;

      FacetsFormComponent.prototype.createURLParameters = function (formData) {
        let params = originalCreateURLParameters.call(this, formData);
        const form = this.refs && this.refs.facetsForm;

        if (form instanceof HTMLFormElement && form.classList.contains('ug-facets-mobile-sort-form')) {
          const sortBy = params.get('sort_by');
          const merged = new URLSearchParams(window.location.search);
          merged.delete('page');
          if (sortBy) {
            merged.set('sort_by', sortBy);
          } else {
            merged.delete('sort_by');
          }
          params = merged;
        }

        const defaultSortBy = getCollectionDefaultSortBy();
        if (defaultSortBy && params.get('sort_by') === defaultSortBy) {
          params.delete('sort_by');
        }

        normalizePriceParams(params);

        return params;
      };

      FacetsFormComponent.prototype.__ugSortPreservePatched = true;
    });
  }

  /** 打开 Filters 后取消价格框焦点，避免手机弹出键盘 */
  function blurDrawerPriceFocus() {
    const drawer = document.querySelector('#filters-drawer dialog.facets--drawer');
    if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      drawer.contains(active) &&
      active.matches('.price-facet__input, .price-facet__range, input[name*="price"]')
    ) {
      active.blur();
    }
  }

  /**
   * section morph 后若筛选 drawer 原本打开，重新打开（对齐 cart-drawer reopen）
   * @param {boolean} wasOpen
   */
  function reopenFiltersDrawerIfNeeded(wasOpen) {
    if (!wasOpen || suppressDrawerReopen) return;
    const host = document.getElementById('filters-drawer');
    if (!host) return;

    if (typeof host.showDialog === 'function') {
      host.showDialog();
      window.requestAnimationFrame(blurDrawerPriceFocus);
      return;
    }

    const dialog = host.querySelector('dialog.facets--drawer');
    if (dialog instanceof HTMLDialogElement && !dialog.open && typeof dialog.showModal === 'function') {
      dialog.showModal();
      window.requestAnimationFrame(blurDrawerPriceFocus);
    }
  }

  function bindDrawerPriceFocusGuard() {
    /** @type {number} */
    let justOpenedUntil = 0;

    const markJustOpened = () => {
      justOpenedUntil = Date.now() + 400;
      window.requestAnimationFrame(blurDrawerPriceFocus);
      window.setTimeout(blurDrawerPriceFocus, 50);
    };

    document.addEventListener(
      'dialog:open',
      (event) => {
        const host = event.target;
        if (!(host instanceof HTMLElement) || host.id !== 'filters-drawer') return;
        markJustOpened();
      },
      true
    );

    document.addEventListener(
      'focusin',
      (event) => {
        if (Date.now() > justOpenedUntil) return;
        const drawer = document.querySelector('#filters-drawer dialog.facets--drawer');
        if (!(drawer instanceof HTMLDialogElement) || !drawer.open) return;
        const target = event.target;
        if (
          target instanceof HTMLElement &&
          drawer.contains(target) &&
          target.matches('.price-facet__input, .price-facet__range, input[name*="price"]')
        ) {
          target.blur();
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllPriceSliders();
      patchDialogForDrawerApply();
      patchDesktopPriceFilterApply();
      patchFacetsUrlParameters();
      bindDrawerPriceFocusGuard();
    });
  } else {
    initAllPriceSliders();
    patchDialogForDrawerApply();
    patchDesktopPriceFilterApply();
    patchFacetsUrlParameters();
    bindDrawerPriceFocusGuard();
  }

  document.addEventListener('shopify:section:load', initAllPriceSliders);
  document.addEventListener('filter:update', () => {
    const drawerDialog = document.querySelector('#filters-drawer dialog.facets--drawer');
    /* suppress 由定时器清除，首次 filter:update 不再立刻清掉，避免并行筛选误 reopen */
    const wasDrawerOpen =
      !suppressDrawerReopen && drawerDialog instanceof HTMLDialogElement && drawerDialog.open;

    afterFilterSectionMorph(() => {
      initAllPriceSliders();
      reopenFiltersDrawerIfNeeded(wasDrawerOpen);
      /* morph 完成后再通知卡片侧刷新（Judge.me 等） */
      document.dispatchEvent(new CustomEvent('ug:filter:morphed'));
    });
  });
})();
